import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button, Typography, Checkbox, FormControlLabel, Box } from '@material-ui/core';

interface DischargeConfirmModalProps {
  patientName?: string;
  onConfirm: () => void;
  onCancel: () => void;
  working?: boolean;
  keepBeacon?: boolean;
}

/**
 * Discharge confirmation modal V2 - Full screen with checkbox
 * Requires checkbox confirmation that bracelet has been removed
 * Original implementation from commit d57cb174e (Jun 24, 2025)
 */
export function PatientDischargeConfirmModal({
  onConfirm,
  onCancel,
  working = false,
  keepBeacon = false,
}: DischargeConfirmModalProps) {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsChecked(e.target.checked);
  };

  const handleConfirmClick = () => {
    if (keepBeacon || isChecked) {
      onConfirm();
    }
  };

  return (
    <FullScreenOverlay
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Content>
        <Title>{keepBeacon ? 'Keep Bracelet With Patient' : 'Bracelet Removed?'}</Title>
        {keepBeacon ? (
          <Typography variant="h4">
            The patient remains in the hospital and keeps the Ospitek bracelet for the next procedure.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <StyledFormControlLabel
              control={
                <StyledCheckbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  color="primary"
                  onClick={e => e.stopPropagation()}
                />
              }
              label="I confirm that I have removed the patient's bracelet"
              onClick={e => e.stopPropagation()}
            />
          </Box>
        )}
        <ButtonGroup>
          <ConfirmButton
            onClick={handleConfirmClick}
            disabled={working || (!keepBeacon && !isChecked)}
            variant="contained"
          >
            {working ? 'Processing...' : 'Confirm Discharge'}
          </ConfirmButton>
          <CancelButton onClick={onCancel} disabled={working} variant="outlined">
            Cancel
          </CancelButton>
        </ButtonGroup>
      </Content>
    </FullScreenOverlay>
  );
}

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.backgroundColor.string()};
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  padding: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4rem;
  max-width: 900px;
  width: 100%;
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.02);
  }
`;

const Title = styled(Typography)`
  && {
    font-size: 5rem;
    text-align: center;
    margin-bottom: 1.5rem;
    font-weight: 700;
    color: #ff4842;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    line-height: 1.2;
    animation: ${pulse} 2.5s ease-in-out infinite;
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  && {
    font-size: 2rem;
    color: white;
    margin: 0;
    padding: 1.5rem 2rem;
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
    cursor: pointer;
    user-select: none;
    min-height: 80px;
    display: flex;
    align-items: center;

    .MuiTypography-root {
      font-size: 2rem;
      line-height: 1.5;
      font-weight: 500;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
      transform: scale(0.98);
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  && {
    transform: scale(1.5);
    margin-right: 1.5rem;
    color: rgba(255, 255, 255, 0.5);
    padding: 12px;

    &.Mui-checked {
      color: #00bcd4;
    }

    &:hover {
      background-color: rgba(0, 188, 212, 0.1);
    }

    .MuiSvgIcon-root {
      font-size: 2rem;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  width: 100%;
  max-width: 900px;
`;

const ConfirmButton = styled(Button)`
  flex: 1;
  padding: 2rem 3rem;
  font-size: 2.25rem;
  background-color: #00bcd4 !important;
  color: white !important;
  font-weight: 700;
  border-radius: 16px;
  min-height: 90px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);

  &:hover {
    background-color: #00acc1 !important;
    box-shadow: 0 6px 16px rgba(0, 188, 212, 0.4);
    transform: translateY(-2px);
    transition: all 0.2s ease;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: rgba(0, 188, 212, 0.2) !important;
    color: rgba(255, 255, 255, 0.3) !important;
    cursor: not-allowed !important;
    opacity: 0.5;
    box-shadow: none;
  }
`;

const CancelButton = styled(Button)`
  flex: 1;
  padding: 2rem 3rem;
  font-size: 2.25rem;
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  font-weight: 700;
  border-radius: 16px;
  min-height: 90px;
  text-transform: none;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
    transform: translateY(-2px);
    transition: all 0.2s ease;
  }

  &:active {
    transform: translateY(0);
  }
`;
